import React, { useEffect, useMemo, useState } from "react";
import {
  fetchHomePhotoConfig,
  saveHomePhotoConfig,
} from "./photoApi";
import {
  buildThumbUrl,
  buildOriginalUrl,
} from "../../../api/publicPhotos";

// Admin Home Photo Control: which recent events (and how many) show in
// the homepage's public "Recent Event Photos" section. The candidate
// list is every event that has approved, event-linked photos; each is
// INCLUDED by default and the newest `maxEvents` included events are
// what unauthed visitors see. Direct gallery links (/photos/<id>) keep
// working even for excluded events — this only controls the homepage.

const fmtDate = (raw, socialEventId) => {
  const src =
    raw ||
    (String(socialEventId || "").match(/^(\d{4}-\d{2}-\d{2})-/) || [])[1];
  if (!src) return "";
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return String(src);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const titleFor = (ev) =>
  ev.title ||
  String(ev.socialEventId || "")
    .replace(/^\d{4}-\d{2}-\d{2}-/, "")
    .replace(/-/g, " ");

const btnStyle = {
  border: "1px solid #d7dbe4",
  background: "#f6f7fa",
  color: "#333",
  borderRadius: 8,
  padding: "6px 14px",
  fontSize: 13,
  cursor: "pointer",
};

const HomePhotoControlTab = () => {
  const [events, setEvents] = useState([]);
  const [excluded, setExcluded] = useState(new Set());
  const [maxEvents, setMaxEvents] = useState(3);
  // Hand-picked homepage preview photos per event:
  // { [socialEventId]: [s3Key, ...] } in display order (max 4).
  // Events without an entry use the automatic pick.
  const [pinned, setPinned] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchHomePhotoConfig()
      .then((data) => {
        if (!alive) return;
        setEvents(Array.isArray(data.events) ? data.events : []);
        setExcluded(new Set(data.config?.excludedEventIds || []));
        setMaxEvents(data.config?.maxEvents || 3);
        setPinned(data.config?.pinnedPhotos || {});
      })
      .catch((err) => {
        if (alive) setError(err.message || "Couldn't load settings.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const markDirty = () => {
    setDirty(true);
    setSavedMsg("");
  };

  const toggle = (id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    markDirty();
  };

  // Pick/unpick a preview photo for an event. Order of picking =
  // display order on the homepage card; capped at 4.
  const togglePin = (eventId, s3Key) => {
    const curr = pinned[eventId] || [];
    let next;
    if (curr.includes(s3Key)) {
      next = curr.filter((k) => k !== s3Key);
    } else if (curr.length >= 4) {
      return; // cap reached — unpick one first
    } else {
      next = [...curr, s3Key];
    }
    setPinned((prev) => {
      const out = { ...prev };
      if (next.length) out[eventId] = next;
      else delete out[eventId];
      return out;
    });
    markDirty();
  };

  const resetPins = (eventId) => {
    setPinned((prev) => {
      const out = { ...prev };
      delete out[eventId];
      return out;
    });
    markDirty();
  };

  const selectAll = () => {
    setExcluded(new Set());
    markDirty();
  };

  const deselectAll = () => {
    setExcluded(new Set(events.map((ev) => ev.socialEventId)));
    markDirty();
  };

  // What unauthed visitors will actually see with the current draft:
  // the newest `maxEvents` included events (same rule the server uses).
  const shownIds = useMemo(() => {
    const cap = Math.min(Math.max(parseInt(maxEvents, 10) || 3, 1), 20);
    return new Set(
      events
        .filter((ev) => !excluded.has(ev.socialEventId))
        .slice(0, cap)
        .map((ev) => ev.socialEventId)
    );
  }, [events, excluded, maxEvents]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const resp = await saveHomePhotoConfig({
        excludedEventIds: [...excluded],
        maxEvents: Math.min(
          Math.max(parseInt(maxEvents, 10) || 3, 1),
          20
        ),
        pinnedPhotos: pinned,
      });
      if (resp?.config) {
        setExcluded(new Set(resp.config.excludedEventIds || []));
        setMaxEvents(resp.config.maxEvents || 3);
        setPinned(resp.config.pinnedPhotos || {});
      }
      setDirty(false);
      setSavedMsg("Saved — the homepage updates immediately.");
    } catch (err) {
      setError(err.message || "Couldn't save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ marginTop: 0 }}>🏠 Home Page Photo Control</h2>
      <p style={{ color: "#6a7385", fontSize: 14 }}>
        Choose which recent events appear in the homepage's public
        "Recent Event Photos" section, and how many. Every event with
        approved photos is included by default; the newest ones (up to
        the limit) are shown. Excluding an event only hides it from the
        homepage — its gallery link keeps working.
      </p>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
      {savedMsg && (
        <div
          style={{
            background: "#eef7ee",
            border: "1px solid #b9dcb5",
            color: "#1f4f1d",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          ✅ {savedMsg}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <label style={{ fontWeight: 600, fontSize: 14 }}>
          Events shown on home page:{" "}
          <input
            type="number"
            min={1}
            max={20}
            value={maxEvents}
            onChange={(e) => {
              setMaxEvents(e.target.value);
              markDirty();
            }}
            style={{ width: 64, padding: "4px 8px" }}
          />
        </label>
        <button type="button" style={btnStyle} onClick={selectAll}>
          Select all
        </button>
        <button type="button" style={btnStyle} onClick={deselectAll}>
          Deselect all
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          style={{
            ...btnStyle,
            background: dirty ? "var(--ab-ocean)" : "#c9cdd6",
            border: "none",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {loading && <p style={{ color: "#6a7385" }}>Loading events…</p>}
      {!loading && !error && events.length === 0 && (
        <p style={{ color: "#6a7385" }}>
          No events with approved public photos yet — tag and approve
          photos in the Photos tab first.
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {events.map((ev) => {
          const id = ev.socialEventId;
          const included = !excluded.has(id);
          const showing = shownIds.has(id);
          const pins = pinned[id] || [];
          const expanded = expandedId === id;
          return (
            <div key={id}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                border: `1px solid ${showing ? "#b9dcb5" : "#e6e9f0"}`,
                background: showing ? "#f4faf4" : "#fff",
                borderRadius: 10,
                padding: "8px 12px",
                cursor: "pointer",
                opacity: included ? 1 : 0.55,
              }}
            >
              <input
                type="checkbox"
                checked={included}
                onChange={() => toggle(id)}
              />
              {ev.previewKey && (
                <img
                  src={buildThumbUrl(ev.previewKey)}
                  alt=""
                  loading="lazy"
                  onError={(e) => {
                    if (e.target.src !== buildOriginalUrl(ev.previewKey)) {
                      e.target.src = buildOriginalUrl(ev.previewKey);
                    }
                  }}
                  style={{
                    width: 52,
                    height: 52,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    textTransform: "capitalize",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {titleFor(ev)}
                </div>
                <div style={{ color: "#6a7385", fontSize: 12 }}>
                  {fmtDate(ev.date, id)} · {ev.photoCount} photo
                  {ev.photoCount === 1 ? "" : "s"}
                </div>
              </div>
              {showing && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#1f4f1d",
                    background: "#dff0dd",
                    borderRadius: 999,
                    padding: "3px 10px",
                    whiteSpace: "nowrap",
                  }}
                >
                  On home page
                </span>
              )}
              <button
                type="button"
                onClick={(e) => {
                  // Inside a <label> — stop the click from also
                  // toggling the include checkbox.
                  e.preventDefault();
                  e.stopPropagation();
                  setExpandedId(expanded ? null : id);
                }}
                style={{
                  ...btnStyle,
                  whiteSpace: "nowrap",
                  background: pins.length ? "#f3ecf7" : btnStyle.background,
                  borderColor: pins.length ? "#c9a8d8" : "#d7dbe4",
                }}
              >
                {pins.length
                  ? `📌 Custom (${pins.length})`
                  : expanded
                  ? "Close"
                  : "Choose photos"}
              </button>
            </label>

            {expanded && (
              <div
                style={{
                  border: "1px solid #e6e9f0",
                  borderTop: "none",
                  borderRadius: "0 0 10px 10px",
                  background: "#fafbfd",
                  padding: "10px 12px",
                  margin: "-6px 0 0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    marginBottom: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 13, color: "#6a7385" }}>
                    Tap up to 4 photos for this event's home-page card —
                    picking order is display order.{" "}
                    {pins.length
                      ? `${pins.length}/4 picked.`
                      : "None picked — automatic selection is used."}
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    {pins.length > 0 && (
                      <button
                        type="button"
                        style={btnStyle}
                        onClick={() => resetPins(id)}
                      >
                        Reset to auto
                      </button>
                    )}
                    <button
                      type="button"
                      style={btnStyle}
                      onClick={() => setExpandedId(null)}
                    >
                      Done
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(90px, 1fr))",
                    gap: 8,
                  }}
                >
                  {(ev.photos || []).map((p) => {
                    const pickIndex = pins.indexOf(p.s3Key);
                    const picked = pickIndex >= 0;
                    return (
                      <button
                        key={p.s3Key}
                        type="button"
                        onClick={() => togglePin(id, p.s3Key)}
                        style={{
                          position: "relative",
                          border: picked
                            ? "3px solid var(--ab-ocean)"
                            : "1px solid #d7dbe4",
                          borderRadius: 8,
                          padding: 0,
                          overflow: "hidden",
                          cursor: "pointer",
                          background: "none",
                          aspectRatio: "1 / 1",
                        }}
                        title={
                          picked
                            ? `Photo ${pickIndex + 1} of ${pins.length} — tap to unpick`
                            : pins.length >= 4
                            ? "4 already picked — unpick one first"
                            : "Tap to pick"
                        }
                      >
                        <img
                          src={buildThumbUrl(p.s3Key)}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            if (
                              e.target.src !== buildOriginalUrl(p.s3Key)
                            ) {
                              e.target.src = buildOriginalUrl(p.s3Key);
                            }
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            opacity:
                              !picked && pins.length >= 4 ? 0.45 : 1,
                          }}
                        />
                        {picked && (
                          <span
                            style={{
                              position: "absolute",
                              top: 4,
                              left: 4,
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              background: "var(--ab-ocean)",
                              color: "#fff",
                              fontSize: 13,
                              fontWeight: 800,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {pickIndex + 1}
                          </span>
                        )}
                        {p.favorite && (
                          <span
                            style={{
                              position: "absolute",
                              bottom: 2,
                              right: 4,
                              color: "#ffd166",
                              fontSize: 14,
                              textShadow: "0 0 3px rgba(0,0,0,0.8)",
                            }}
                            aria-hidden
                          >
                            ★
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HomePhotoControlTab;
